import React, { useState, useEffect, useRef } from "react";
import { List, InfiniteScroll } from "antd-mobile";
import StationItem from "@/component/station-item";
import { trackGdp } from "@/utils/gio-report";
import { Toast } from "@xp-design/mobile";
import { queryStationList } from "@/api/configPage";
import "./index.less";
import { NativeByUrlList } from "@/utils/common";
import { getChannel } from "@/utils/tools";
import { processOssImage } from "@/utils/image";

interface FixedStationProps {
  activityInfo: any;
  className?: string;
}

interface StationListItem {
  stationId: number;
  stationName: string;
  stationAddr: string;
  distance: number;
  stationAvailabilityDesc: string;
  stationLabelList: any[];
  stationBillingInfo: any;
  stationActivityInfoV5: any;
  stationEquipOverViewList: any[];
}

const FixedStation: React.FC<FixedStationProps> = ({
  activityInfo,
  className = "",
}) => {
  const [data, setData] = useState<StationListItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [stationIds, setStationIds] = useState([]);
  const [bgUrl, setBgUrl] = useState(null);
  // 新增限制相关状态
  const [isLimit, setIsLimit] = useState(false);
  const [limitNum, setLimitNum] = useState(0);
  const [jumpLinkList, setJumpLinkList] = useState({});
  // 动态计算 pageSize
  const pageSize = isLimit && limitNum ? Math.min(limitNum, 5) : 5;

  // 计算总页数和是否还有更多数据
  const totalPages = Math.ceil(stationIds.length / pageSize);

  useEffect(() => {
    const infoData = activityInfo.moduleJsonData.base;
    // 限制配置
    setIsLimit(infoData[0]?.isLimit);
    setLimitNum(infoData[0]?.visibleCount);
    setJumpLinkList(infoData[0]?.jumpUrlMap);
    // 场站ID
    const stationConditionsData = infoData[0]?.stationConditionsData;
    if (stationConditionsData && Array.isArray(stationConditionsData)) {
      const appointStationRule = stationConditionsData.find(
        (item) => item.ruleType === "APPOINT_STATION",
      );
      if (
        appointStationRule &&
        appointStationRule.ruleParam &&
        appointStationRule.ruleParam.valueList
      ) {
        let ids = appointStationRule.ruleParam.valueList;
        if (infoData[0]?.isLimit && infoData[0]?.visibleCount) {
          ids = ids.slice(0, infoData[0].visibleCount);
        }
        setStationIds(ids);
      }
    }
    // 背景图
    const bgImageUrl = infoData[1]?.val;
    if (bgImageUrl) {
      setBgUrl(bgImageUrl);
    }
  }, [activityInfo]);

  // 初始化数据
  useEffect(() => {
    console.log("FixedStation", stationIds);
    if (stationIds.length > 0) {
      setCurrentPage(0);
      setData([]);
      setHasMore(true);
      loadMoreStations(true);
    } else {
      setData([]);
      setHasMore(false);
    }
  }, [stationIds]);

  // 获取当前页的站点ID列表
  const getCurrentPageStationIds = (page: number): number[] => {
    const startIndex = page * pageSize;
    // 限制最大展示数
    const endIndex = Math.min(startIndex + pageSize, stationIds.length);
    return stationIds.slice(startIndex, endIndex);
  };

  // 加载更多站点数据
  const loadMoreStations = async (resetData = false) => {
    const pageToLoad = resetData ? 0 : currentPage + 1;

    if (loading || pageToLoad >= totalPages) return;

    const currentPageIds = getCurrentPageStationIds(pageToLoad);
    if (currentPageIds.length === 0) return;

    setLoading(true);
    setError("");

    try {
      const res = await queryStationList({
        stationIdList: currentPageIds.map(String), // 转换为字符串数组
      });

      console.log("queryStationList response:", res);

      if (res.code === 200) {
        if (resetData) {
          setData(res.data.stationList);
        } else {
          setData((prevData) => [...prevData, ...res.data.stationList]);
        }

        setCurrentPage(pageToLoad);
        setHasMore(pageToLoad + 1 < totalPages);
      } else {
        throw new Error(`获取站点详情失败: ${res.code}`);
      }
    } catch (err: any) {
      console.error("queryStationList error:", err);
      setError(err.message || "加载失败");
      Toast.show({
        content: err.message || "加载失败，请重试",
        duration: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  // 重新加载
  const handleRetry = () => {
    setCurrentPage(0);
    setData([]);
    setHasMore(stationIds.length > pageSize);
    setError("");
    loadMoreStations(true);
  };

  // 渲染充电站项
  const renderStationItem = (station: StationListItem, index: number) => {
    const stationProps = {
      station,
      showDistance: true,
      withLine: index < data.length - 1,
      showAddress: true,
      showAct: true,
      showTab: true,
      from: "fixedStation",
    };

    const handleItemClick = () => {
      trackGdp("P20189", "B004", {
        trackName: "充电_营销专题页_组件点击",
        origin: getChannel(),
        userType: "",
        versionId: activityInfo.versionCode,
        pageId: activityInfo.pageCode,
        ComponentCode: activityInfo.moduleId,
        type: activityInfo.moduleType,
        comp_style: JSON.parse(activityInfo.moduleJson).visual?.visualData[0]
          ?.typeName,
        clickLocation: station.stationId,
      });
    };

    return (
      <div
        key={station.stationId}
        className="fixed-station-item"
        onClick={handleItemClick}
      >
        <StationItem {...stationProps} />
      </div>
    );
  };

  return (
    <div
      className={`fixed-station-container ${className}`}
      ref={containerRef}
      style={{
        backgroundColor: bgUrl ? "#ffffff" : "transparent", // 默认白色背景
        backgroundImage: bgUrl ? `url(${processOssImage(bgUrl)})` : "none",
        backgroundSize: "100% auto", // 宽度100%，高度自动，保持宽高比
        backgroundPosition: "center top",
        backgroundRepeat: "repeat-y", // 垂直方向重复
      }}
    >
      <div className="fixed-station-list">
        {data?.length === 0 && !loading && (
          <div className="empty-container">
            <span>暂无推荐充电站</span>
          </div>
        )}

        {data?.length > 0 && (
          <List>
            {data.map((station, index) => renderStationItem(station, index))}
            <InfiniteScroll
              loadMore={() => loadMoreStations(false)}
              hasMore={hasMore}
              threshold={50}
            >
              {loading && (
                <div className="loading-container">
                  <div className="loading-spinner" />
                  <span>加载中...</span>
                </div>
              )}

              {error && (
                <div className="error-container">
                  <span className="error-text">{error}</span>
                  <button className="retry-button" onClick={handleRetry}>
                    重试
                  </button>
                </div>
              )}

              {!hasMore && data.length > 0 && (
                <div
                  className="no-more-container"
                  onClick={() => NativeByUrlList(jumpLinkList)}
                >
                  <span>
                    {isLimit && limitNum && data.length >= limitNum
                      ? `查看更多场站`
                      : "没有更多数据了"}
                  </span>
                  {isLimit && limitNum && data.length >= limitNum ? (
                    <img
                      src={processOssImage(
                        "https://s.xiaopeng.com/xp-charge/react-native/pile/more_icon_v2.png",
                      )}
                      className={"moreIcon"}
                    ></img>
                  ) : null}
                </div>
              )}
            </InfiniteScroll>
          </List>
        )}
      </div>
    </div>
  );
};

export default FixedStation;
